import {
  useMutation,
  UseMutationResult,
  useQueryClient,
} from "@tanstack/react-query";
import { useApi } from "../../api/ApiProvider";
import { rxKeys } from "./rx.keys";
import {
  PatientDto,
  RxDto,
  RxDtoProcessingStatusEnum,
  RxSeriesDto,
  InsurerDto,
} from "../../api-client";
import { resourceUsage } from "process";
import { appointmentKeys } from "../Appointment/appointment.keys";
import { transactionKeys } from "../Transactions/transaction.keys";
import { calendarKeys, invalidateCalendarAppointmentCaches } from "../Calendar/calendar.keys";
import useStore from "../../helpers/useStore";
import { PatientsContext } from "../Patients/patients.provider";
import { patientKeys } from "../Patients/patient.keys";
import { selectSavedRx } from "./selectSavedRx";
import dayjs from "dayjs";
import notificationStore from "../Notification/notificationStore";
import { toRxWriteDto } from "../../helpers/rxWriteDto";

/**
 * NOTE: Global error/success handling is configured in src/stores/queryClient.ts
 * - MutationCache handles automatic error notifications via notificationStore
 * - MutationCache handles automatic success notifications via meta.successMessage
 * - No need to manually call notificationStore in individual mutations
 */

/**
 * Mutation to update RX processing status
 * Replaces RxStore.updateprocessingStatus
 */
export const useUpdateRxProcessingStatus = () => {
  const { rx: rxApi } = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      rxId,
      status,
      appointmentIds,
      patientId,
    }: {
      rxId: string;
      status: RxDtoProcessingStatusEnum;
      appointmentIds: string[];
      patientId: number;
    }) => {
      return await rxApi.updateProcessingStatus(rxId, status, appointmentIds);
    },
    onSuccess: (data, variables) => {
      // Invalidate the specific RX query to refetch updated data
      queryClient.invalidateQueries({
        queryKey: rxKeys.rx.detail(variables.rxId),
      });

      // Also invalidate any related RX lists that might show this RX
      queryClient.invalidateQueries({
        queryKey: rxKeys.rxIds.all(),
      });

      // Invalidate RX light lists (used by RxSelectorCard)
      queryClient.invalidateQueries({
        queryKey: rxKeys.rxList.all(),
      });

      // Invalidate RX series queries as they might also be affected
      queryClient.invalidateQueries({
        queryKey: rxKeys.rxSeries.all(),
      });
      queryClient.invalidateQueries({
        queryKey: appointmentKeys.appointmentsByRx.detail(variables.rxId),
      });
      if (variables.patientId) {
        queryClient.invalidateQueries({
          queryKey: appointmentKeys.appointmentsByPatient.detail(
            variables.patientId,
          ),
        });
        queryClient.invalidateQueries({
          queryKey: appointmentKeys.continuousAppointmentsByPatient.detail(
            variables.patientId.toString(),
          ),
        });
        queryClient.invalidateQueries({
          queryKey: appointmentKeys.cancelledAppointmentsByPatient.detail(
            variables.patientId,
          ),
        });
        queryClient.invalidateQueries({
          queryKey: appointmentKeys.appointmentsBySeries.all(),
        });
        queryClient.invalidateQueries({
          queryKey: calendarKeys.unscheduledPatientAppointments.detail(
            variables.patientId,
          ),
        });
        queryClient.invalidateQueries({
          queryKey: calendarKeys.unscheduledContinuousAppointments.detail(
            variables.patientId,
          ),
        });
        queryClient.invalidateQueries({
          queryKey: calendarKeys.continuousAppointmentsForPatient.detail(
            variables.patientId,
          ),
        });
        queryClient.invalidateQueries({
          queryKey: appointmentKeys.inactiveAppointmentsCount.detail(
            variables.patientId.toString(),
          ),
        });
        queryClient.invalidateQueries({
          queryKey: appointmentKeys.filteredAppointmentsByPatient.all(),
        });
        invalidateCalendarAppointmentCaches(queryClient);
      }
    },
    meta: {
      successMessage: "Status erfolgreich aktualisiert",
      errorMessage: "Fehler beim Aktualisieren des Status",
    },
  });
};

/**
 * Mutation to update RX processing status with optional cleanup of unscheduled appointments.
 * Used after swap when setting an Rx to "abgeschlossen" and unscheduled timeslots need to be removed.
 */
export const useUpdateRxProcessingStatusWithCleanup = () => {
  const { rx: rxApi } = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      rxId,
      status,
      patientId,
      removeUnscheduled,
    }: {
      rxId: string;
      status: RxDtoProcessingStatusEnum;
      patientId: number;
      removeUnscheduled?: boolean;
    }) => {
      return await rxApi.updateProcessingStatus(
        rxId,
        status,
        [],
        removeUnscheduled,
      );
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({
        queryKey: rxKeys.rx.detail(variables.rxId),
      });
      queryClient.invalidateQueries({ queryKey: rxKeys.rxIds.all() });
      queryClient.invalidateQueries({ queryKey: rxKeys.rxList.all() });
      queryClient.invalidateQueries({ queryKey: rxKeys.rxSeries.all() });
      queryClient.invalidateQueries({
        queryKey: appointmentKeys.appointmentsByRx.detail(variables.rxId),
      });
      if (variables.patientId) {
        queryClient.invalidateQueries({
          queryKey: appointmentKeys.appointmentsByPatient.detail(
            variables.patientId,
          ),
        });
        queryClient.invalidateQueries({
          queryKey: appointmentKeys.appointmentsBySeries.all(),
        });
        queryClient.invalidateQueries({
          queryKey: calendarKeys.unscheduledPatientAppointments.detail(
            variables.patientId,
          ),
        });
        queryClient.invalidateQueries({
          queryKey: appointmentKeys.inactiveAppointmentsCount.detail(
            variables.patientId.toString(),
          ),
        });
        queryClient.invalidateQueries({
          queryKey: appointmentKeys.filteredAppointmentsByPatient.all(),
        });
      }
    },
    meta: {
      // No automatic success message — the PostSwapRxCompletionDialog handles notifications
      errorMessage: "Fehler beim Aktualisieren des Rx-Status",
    },
  });
};

/*  public copyToContinous = async (rxId: string) => {
    const { data } = (await this.apiClient.copyToContinuous(rxId)) as any;
    return data || [];
  };*/

export const useCopyToContinuous = () => {
  const { rx: rxApi } = useApi();
  const queryClient = useQueryClient();

  type Vars = { rxId: string; patientId: number };

  return useMutation({
    mutationKey: ["rx", "copyToContinuous"],
    mutationFn: ({ rxId }: Vars) => rxApi.copyToContinuous(rxId),
    // Invalidate only after success (you usually don't want to refetch on failure)
    onSuccess: async (_data, { rxId, patientId }) => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: rxKeys.rx.detail(rxId),
          refetchType: "active",
        }),
        queryClient.invalidateQueries({
          queryKey: rxKeys.rxList.forPatient(patientId),
        }),
        queryClient.invalidateQueries({
          queryKey: rxKeys.rxSeriesList.forPatient(patientId),
          refetchType: "active",
        }),
        queryClient.invalidateQueries({
          queryKey: rxKeys.rxSeries.all(),
        }),
        queryClient.invalidateQueries({
          queryKey: appointmentKeys.appointmentsByRx.detail(rxId),
        }),
        queryClient.invalidateQueries({
          queryKey: appointmentKeys.appointmentsByPatient.detail(patientId),
        }),
        queryClient.invalidateQueries({
          queryKey: rxKeys.rxLackingAppointments.forPatient(patientId),
          refetchType: "active",
        }),
      ]);
    },
    meta: {
      successMessage: "Rezept erfolgreich in ein Dauerrezept umgewandelt",
      errorMessage: "Fehler beim Umwandeln des Rezepts in ein Dauerrezept",
    } as const,
  });
};

//This for saving individual rx but our cuurent api takes array of rxs (refactor to be done on api side)
export const useSaveRxs = () => {
  const { rx: rxApi } = useApi();
  const queryClient = useQueryClient();
  const patientStore = useStore(PatientsContext);

  type Vars = { patientId: number; rxs: RxDto[] };
  return useMutation({
    mutationKey: ["rx", "patientSaveRxs"],
    mutationFn: ({ patientId, rxs }: Vars) =>
      rxApi.patientSaveRxs(patientId, rxs.map(toRxWriteDto)),

    onSuccess: async (_data, { patientId, rxs }) => {
      /**
       * MD-389: surface the per-Rx validation notices the save returns.
       *
       * `patientSaveRxs` answers with `SaveRxResponse[]`, and each entry
       * carries `rx.messages` — the credential mismatches from the MD-358
       * re-resolution ("Termin am … bei …: Heilmittel … kann mit der
       * hinterlegten Qualifikation nicht erbracht werden"), the
       * series-resolution notices, and the visitFee-not-found warnings.
       *
       * Nothing on this path ever read them. The API builds and returns them
       * correctly, `meta.successMessage` then reported a flat "Rezept
       * gespeichert", and every notice was discarded — so changing a Heilmittel
       * to one the assigned therapist may not deliver looked like a clean save
       * while the appointments were silently left behind.
       *
       * Shown as a warning: these describe something the user must act on, not
       * something that succeeded. The generic success toast still fires from
       * the MutationCache, which is correct — the Rx really was saved.
       *
       * Except notices the API marks `severity: "info"` — the series an Rx was
       * attached to is a routine outcome, not a problem. Those confirm the save
       * in green, and only when there is no warning to show: the store holds one
       * message, so they would otherwise replace "Rezept gespeichert" or bury a
       * real warning.
       */
      const collect = (isInfo: boolean) =>
        Array.from(
          new Set(
            (_data ?? [])
              .flatMap((entry: any) => entry?.rx?.messages ?? [])
              .filter((m: any) => (m?.severity === "info") === isInfo)
              .map((m: any) => (typeof m === "string" ? m : m?.message))
              .filter((m: any): m is string => typeof m === "string" && m.trim() !== ""),
          ),
        );
      const warnings = collect(false);
      const notices = collect(true);
      if (warnings.length) {
        notificationStore.showMessage(warnings.join("\n"), "warning");
      } else if (notices.length) {
        notificationStore.showMessage(["Rezept gespeichert.", ...notices].join(" "), "success");
      }

      // console.log(
        // "Rx save successful, invalidating queries for patientId:",
        // patientId,
        // "and rxs:",
        // _data,
      // );
      if (
        _data &&
        _data[0]?.validRxSeriesIds &&
        _data[0]?.validRxSeriesIds.length > 0
      ) {
        patientStore.setPreflightDialogOpen(
          true,
          _data[0]?.validRxSeriesIds || [],
        );
      }

      const extractId = (value: unknown): string | undefined =>
        typeof value === "string"
          ? value
          : (value as { id?: string } | null | undefined)?.id;
      /**
       * Check if any saved Rx is copayment-exempt and patient lacks a matching exemption
       * Skip for minors — they are auto-exempt and don't need exemption records
       * BG (Berufsgenossenschaft) and Bundeswehr Rxs are automatically copayment-exempt
       * (by rxType, or by BG tarif group); they never require an exemption record,
       * so they must not trigger the exemption dialog.
       * Note: depending on the serialization path the rxType can come back as `rxtype` or
       * `type`, and the tarif group as `tarifGroupId` or `tarifGroup`, and each may be a
       * plain id string OR an object `{ id }`. Normalize all of these to a plain id.
       */
      const isAutoCopaymentExempt = (rx?: RxDto) => {
        const r = rx as unknown as {
          rxtype?: unknown;
          type?: unknown;
          tarifGroupId?: unknown;
          tarifGroup?: unknown;
        };
        const rxTypeId = extractId(r?.rxtype) ?? extractId(r?.type);
        const tarifGroupId =
          extractId(r?.tarifGroupId) ?? extractId(r?.tarifGroup);
        return (
          rxTypeId === "Berufsgenossenschaft" ||
          rxTypeId === "Bundeswehr" ||
          tarifGroupId === "BG"
        );
      };

      const hasCopaymentExemptedRx = _data?.some(
        (r) => r.rx?.isCopaymentExempted && !isAutoCopaymentExempt(r.rx),
      );
      if (hasCopaymentExemptedRx) {
        try {
          // The patient detail is cached under a key that also carries a trailing
          // { locationId } segment (see usePatient), so an exact getQueryData lookup
          // with the bare detail key never matches. Use a partial (prefix) match so
          // we find the patient regardless of the locationId segment.
          const cachedPatient = queryClient
            .getQueriesData<PatientDto>({
              queryKey: patientKeys.patient.detail(patientId),
            })
            .map(([, data]) => data)
            .find((data): data is PatientDto => Boolean(data));

          // Minors are automatically exempt, no need to prompt for exemption records
          if (cachedPatient?.dob) {
            const isMinor = dayjs().diff(dayjs(cachedPatient.dob), "year") < 18;
            if (isMinor) {
              return;
            }
          }
          const exemptions = cachedPatient?.copaymentExemptions || [];
          const rxDate = _data?.find(
            (r) => r.rx?.isCopaymentExempted && !isAutoCopaymentExempt(r.rx),
          )?.rx?.date;
          const hasMatchingExemption = exemptions.some((ex) => {
            if (!ex.startDate || !ex.endDate) return false;
            const start = new Date(ex.startDate);
            const end = new Date(ex.endDate);
            const check = rxDate ? new Date(rxDate) : new Date();
            return check >= start && check <= end;
          });
          if (!hasMatchingExemption) {
            patientStore.setCopaymentExemptionDialogOpen(true, patientId);
          }
        } catch {
          // If patient data isn't cached, still show the dialog to be safe
          patientStore.setCopaymentExemptionDialogOpen(true, patientId);
        }
      }

      // For a saved Kasse Rx, prompt to copy its insuree number/payer onto the
      // patient when missing or different. Identify it from input `rxs`.
      const getRxTypeId = (rxtype: unknown): string | undefined =>
        typeof rxtype === "string"
          ? rxtype
          : (rxtype as { id?: string } | undefined)?.id;
      const kasseRx = rxs?.find((r) => getRxTypeId(r.rxtype) === "Kasse");
      if (kasseRx) {
        // The input rx's nested `payer` can be stale (the form edits it via
        // `Receiptpayer`), so use the values the server sent back after saving.
        const savedRxs = (_data ?? [])
          .map((d) => d.rx)
          .filter((rx): rx is RxDto => rx != null);
        // MD-383: the response carries one entry PER THERAPY LINE, so counting
        // entries could not tell one Rx from several — a two-Heilmittel Rx lost
        // its payer here, and with it the Kostenträger line in the dialog.
        const savedKasseRx = selectSavedRx(savedRxs, kasseRx);

        // The full patient is cached under a key that includes `locationId`,
        // so getQueriesData (prefix match) is used instead of getQueryData.
        const cachedPatient = queryClient
          .getQueriesData<PatientDto>({
            queryKey: patientKeys.patient.detail(patientId),
          })
          .map(([, data]) => data)
          .find((data) => data != null);
        const rxInsureeNumber =
          (savedKasseRx?.insureeNumber ?? kasseRx.insureeNumber)?.trim() ||
          null;
        const patientInsuranceNo = cachedPatient?.insuranceNo?.trim() || null;
        const rxPayer: InsurerDto | null = savedKasseRx?.payer ?? null;
        const patientPayer: InsurerDto | null = cachedPatient?.payer ?? null;
        // The patient stores its insurance status (Versichertenstatus) in `status`.
        const rxInsuranceStatus =
          (savedKasseRx?.insuranceStatus ?? kasseRx.insuranceStatus)?.trim() ||
          null;
        const patientInsuranceStatus = cachedPatient?.status?.trim() || null;

        const insureeNumberDiffers =
          !!rxInsureeNumber &&
          (!patientInsuranceNo || patientInsuranceNo !== rxInsureeNumber);
        const payerDiffers =
          !!rxPayer?.id && (!patientPayer?.id || patientPayer.id !== rxPayer.id);
        const insuranceStatusDiffers =
          !!rxInsuranceStatus &&
          (!patientInsuranceStatus ||
            patientInsuranceStatus !== rxInsuranceStatus);

        if (insureeNumberDiffers || payerDiffers || insuranceStatusDiffers) {
          patientStore.setInsuranceDataLiftoverDialogOpen(
            true,
            patientId,
            rxInsureeNumber,
            patientInsuranceNo,
            rxPayer,
            patientPayer,
            rxInsuranceStatus,
            patientInsuranceStatus,
          );
        }
      }

      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: rxKeys.rxList.forPatient(patientId),
          refetchType: "active",
        }),
        queryClient.invalidateQueries({
          queryKey: rxKeys.rxSeriesList.forPatient(patientId),
          refetchType: "active",
        }),
        queryClient.invalidateQueries({
          queryKey: rxKeys.rx.detail(rxs[0]?.id || ""),
          refetchType: "active",
        }),
        queryClient.invalidateQueries({
          queryKey: rxKeys.rxLackingAppointments.forPatient(patientId),
          refetchType: "active",
        }),
        queryClient.invalidateQueries({
          queryKey: rxKeys.therapyRxSeries.forPatient(patientId),
          refetchType: "active",
        }),
        queryClient.invalidateQueries({
          queryKey: rxKeys.rxSeries.all(),
          refetchType: "active",
        }),
        queryClient.invalidateQueries({
          queryKey: transactionKeys.patientBillsInfo.forPatient(patientId),
          refetchType: "active",
        }),
        queryClient.invalidateQueries({
          queryKey: transactionKeys.unpaidBadgeCount.forPatient(patientId),
          refetchType: "active",
        }),
        queryClient.invalidateQueries({
          queryKey: appointmentKeys.appointmentsByPatient.detail(patientId),
        }),
        queryClient.invalidateQueries({
          queryKey: appointmentKeys.appointmentsBySeries.all(),
        }),
        queryClient.invalidateQueries({
          queryKey: appointmentKeys.continuousAppointmentsByPatient.detail(
            patientId.toString(),
          ),
        }),
        queryClient.invalidateQueries({
          queryKey:
            appointmentKeys.cancelledAppointmentsByPatient.detail(patientId),
        }),
        queryClient.invalidateQueries({
          queryKey: appointmentKeys.appointmentsByRx.detail(rxs[0]?.id || ""),
        }),
        queryClient.invalidateQueries({
          queryKey:
            calendarKeys.unscheduledPatientAppointments.detail(patientId),
        }),
        queryClient.invalidateQueries({
          queryKey:
            calendarKeys.unscheduledContinuousAppointments.detail(patientId),
        }),
        queryClient.invalidateQueries({
          queryKey:
            calendarKeys.continuousAppointmentsForPatient.detail(patientId),
        }),
        invalidateCalendarAppointmentCaches(queryClient),
      ]);
    },
    meta: {
      successMessage: "Rezept gespeichert",
      errorMessage: "Fehler beim Speichern des Rezepts",
    } as const,
  });
};

export const useDeleteRx = () => {
  const { rx: rxApi } = useApi();
  const queryClient = useQueryClient();
  type Vars = { rxId: string; patientId: number };
  return useMutation({
    mutationKey: ["rx", "deleteRx"],
    mutationFn: ({ rxId }: Vars) => {
      return rxApi.deleteRx(rxId);
    },
    onSuccess: async (data, variables) => {
      // The endpoint returns 200 even when deletion is blocked by a policy
      // check (patient bill, attended appointments, insurance bill). Surface
      // the reason(s) and skip cache invalidation when nothing was deleted.
      if (!data?.deleted) {
        const blockerMessage =
          data?.blockers?.map((b) => b.message).join(" ") ||
          "Rezept konnte nicht gelöscht werden!";
        notificationStore.showMessage(blockerMessage, "error");
        return;
      }
      notificationStore.showMessage("Rezept erfolgreich gelöscht", "success");
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: rxKeys.rx.detail(variables.rxId),
        }),
        queryClient.invalidateQueries({
          queryKey: rxKeys.rxList.forPatient(variables.patientId),
          refetchType: "active",
        }),
        queryClient.invalidateQueries({
          queryKey: rxKeys.rxSeriesList.forPatient(variables.patientId),
          refetchType: "active",
        }),
      ]);
    },
  });
};

export const useUpdateSeries = () => {
  const { rx: rxApi } = useApi();
  const queryClient = useQueryClient();
  type Vars = { seriesId: string; patientId: number; seriesData: RxSeriesDto };
  return useMutation({
    mutationKey: ["rxSeries", "updateSeries"],
    mutationFn: ({ seriesId, patientId, seriesData }: Vars) => {
      return rxApi.updateSeries(seriesId, patientId, seriesData);
    },
    onSuccess: async (data, variables) => {
      // Also invalidate any related RX lists that might show this RX series
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: rxKeys.rxSeries.detail(variables.seriesId),
        }),
        queryClient.invalidateQueries({
          queryKey: rxKeys.rxSeriesList.forPatient(variables.patientId),
          refetchType: "active",
        }),
        queryClient.invalidateQueries({
          queryKey: rxKeys.therapyRxSeries.forPatient(variables.patientId),
          refetchType: "active",
        }),
        queryClient.invalidateQueries({
          queryKey: rxKeys.rxList.forPatient(variables.patientId),
          refetchType: "active",
        }),
      ]);
    },
    meta: {
      successMessage: "Serie erfolgreich aktualisiert",
      errorMessage: "Fehler beim Aktualisieren der Serie",
    },
  });
};

export const useDeleteSeries = () => {
  const { rx: rxApi } = useApi();
  const queryClient = useQueryClient();
  type Vars = { seriesId: string; patientId: number };
  return useMutation({
    mutationKey: ["rxSeries", "deleteSeries"],
    mutationFn: ({ seriesId, patientId }: Vars) => {
      return rxApi.deleteSeries(seriesId);
    },
    onSuccess: async (data, variables) => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: rxKeys.rxSeries.detail(variables.seriesId),
        }),
        queryClient.invalidateQueries({
          queryKey: rxKeys.rxSeriesList.forPatient(variables.patientId),
          refetchType: "active",
        }),
        queryClient.invalidateQueries({
          queryKey: rxKeys.rxList.forPatient(variables.patientId),
          refetchType: "active",
        }),
      ]);
    },
    meta: {
      successMessage: "Serie erfolgreich gelöscht",
      errorMessage: "Fehler beim Löschen der Serie",
    },
  });
};

export const useUpdateSeriesForTherapyRx = () => {
  const { rx: rxApi } = useApi();
  const queryClient = useQueryClient();
  type Vars = { seriesId: string; therapyRxId: string; patientId: number };
  return useMutation({
    mutationKey: ["rxSeries", "updateSeriesForTherapyRx"],
    mutationFn: ({ therapyRxId, seriesId, patientId }: Vars) => {
      return rxApi.updateSeriesForTherapyRx(therapyRxId, seriesId);
    },
    onSuccess: async (data, variables) => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: rxKeys.rxSeries.detail(variables.seriesId),
        }),
        queryClient.invalidateQueries({
          queryKey: rxKeys.rxSeriesList.forPatient(variables.patientId),
          refetchType: "active",
        }),
        queryClient.invalidateQueries({
          queryKey: appointmentKeys.appointmentsBySeries.all(),
        }),
        queryClient.invalidateQueries({
          queryKey: rxKeys.rxList.forPatient(variables.patientId),
          refetchType: "active",
        }),
      ]);
    },
    meta: {
      successMessage: "Serie erfolgreich aktualisiert",
      errorMessage: "Fehler beim Aktualisieren der Serie",
    },
  });
};
export const useAddNewSeriesForPatient = () => {
  const { rx: rxApi } = useApi();
  const queryClient = useQueryClient();
  type Vars = { patientId: number; seriesData: RxSeriesDto };
  return useMutation({
    mutationKey: ["rxSeries", "addNewSeriesForPatient"],
    mutationFn: ({ patientId, seriesData }: Vars) => {
      return rxApi.addNewSeriesToPatient(patientId, seriesData);
    },
    onSuccess: async (data, variables) => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: rxKeys.rxSeriesList.forPatient(variables.patientId),
          refetchType: "active",
        }),
        queryClient.invalidateQueries({
          queryKey: rxKeys.rxList.forPatient(variables.patientId),
          refetchType: "active",
        }),
      ]);
    },

    meta: {
      successMessage: "Neue Serie erfolgreich hinzugefügt",
      errorMessage: "Fehler beim Hinzufügen der neuen Serie",
    },
  });
};
